"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _matchSorter = _interopRequireDefault(require("match-sorter"));

var _reactTable = require("react-table");

var _propTypes2 = _interopRequireDefault(require("../../../constants/propTypes"));

var _Header = _interopRequireDefault(require("./PrivateComponents/Header"));

var _Footer = _interopRequireDefault(require("./PrivateComponents/Footer"));

var _NoRecords = _interopRequireDefault(require("./PrivateComponents/NoRecords"));

var _LoadingSpinner = _interopRequireDefault(require("../../IconicDisplayElements/LoadingSpinner"));

var _DataGrid = require("./DataGrid.styles");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/**
 * The Data Grid is a component to show large amount of data,
 * especially if it will need things like pagination, filtering and sorting.
 * It requires both column and data props and optionally takes a config object.
 *
 * Please look at the props sections for a more in depth explanation of each prop.
 *
 * In general columns is an array of objects with accessor and Header keys.
 *
 * * [
 * {
 *   accessor: 'name',
 *   Header: 'Names'
 * },
 * ]
 *
 * data will be an array of objects where the keys match column accessor and the value is the data to be shown
 *
 *  * [{
 *   name: 'Jim',
 *   age: 39
 * }]
 *
 */
var DataGrid = _react.default.memo(function (_ref) {
  var columns = _ref.columns,
      config = _ref.config,
      data = _ref.data,
      isLoading = _ref.isLoading,
      withShadow = _ref.withShadow;

  // Default Columns turns off filters by default
  var defaultColumn = _react.default.useMemo(function () {
    return {
      disableFilters: true
    };
  }, []); // Create the Table instance with react-table


  var _useTable = (0, _reactTable.useTable)({
    columns: columns,
    data: data,
    defaultColumn: defaultColumn,
    initialState: {
      pageSize: config.pageSize ? config.pageSize : data.length
    }
  }, _reactTable.useFilters, _reactTable.useSortBy, _reactTable.usePagination),
      getTableProps = _useTable.getTableProps,
      headerGroups = _useTable.headerGroups,
      prepareRow = _useTable.prepareRow,
      page = _useTable.page,
      canPreviousPage = _useTable.canPreviousPage,
      canNextPage = _useTable.canNextPage,
      pageOptions = _useTable.pageOptions,
      gotoPage = _useTable.gotoPage,
      nextPage = _useTable.nextPage,
      previousPage = _useTable.previousPage,
      setPageSize = _useTable.setPageSize,
      _useTable$state = _useTable.state,
      pageIndex = _useTable$state.pageIndex,
      pageSize = _useTable$state.pageSize; // If there isn't a searchColumn add one. This is what the search bar is using


  var found = columns.find(function (col) {
    return col.id === 'searchColumn';
  });

  if (!found) {
    columns.push({
      Header: 'All',
      id: 'searchColumn',
      Filter: function Filter() {},
      filter: function filter(rows, id, filterValue) {
        if (config.searchColumns) {
          return (0, _matchSorter.default)(rows, filterValue, {
            keys: config.searchColumns.map(function (col) {
              return "values.".concat(col);
            })
          });
        }

        return rows;
      },
      disableFilters: false,
      filterAll: true,
      show: false
    });
  }

  var getTableContent = function getTableContent() {
    // regardless of existing content, if the loading flag is set, show the loading state
    // this supports the concept of loading in new data over existing, providing visual context that something is happening
    if (isLoading || data.length) {
      return /*#__PURE__*/_react.default.createElement(_DataGrid.TableArea, getTableProps(), /*#__PURE__*/_react.default.createElement(_DataGrid.TableContent, {
        isLoading: isLoading,
        hasData: !!data.length
      }, page.map(function (row) {
        return prepareRow(row) || /*#__PURE__*/_react.default.createElement(_DataGrid.TableRow, row.getRowProps(), row.cells.map(function (cell) {
          return /*#__PURE__*/_react.default.createElement(_DataGrid.Cell, _extends({
            alignment: cell.column.alignment
          }, cell.getCellProps()), /*#__PURE__*/_react.default.createElement(_DataGrid.CellTitle, null, "".concat(cell.column.Header, ":")), /*#__PURE__*/_react.default.createElement(_DataGrid.CellValue, null, cell.render('Cell')));
        }));
      })), isLoading && /*#__PURE__*/_react.default.createElement(_DataGrid.LoaderMask, null, /*#__PURE__*/_react.default.createElement(_LoadingSpinner.default, {
        dataTestId: "datagrid-loading-spinner:"
      })));
    }

    return /*#__PURE__*/_react.default.createElement(_NoRecords.default, {
      noRecordsText: config.noRecordsText
    });
  };

  var getFooterContent = function getFooterContent() {
    // if we have a custom footer, or if we have data + pagination, render the footer
    if (config.customFooter || data.length && config.paginate) {
      return /*#__PURE__*/_react.default.createElement(_Footer.default, {
        config: config,
        pageSize: pageSize,
        setPageSize: setPageSize,
        data: data,
        gotoPage: gotoPage,
        pageOptions: pageOptions,
        pageIndex: pageIndex,
        previousPage: previousPage,
        canPreviousPage: canPreviousPage,
        nextPage: nextPage,
        canNextPage: canNextPage
      }, config.customFooter);
    }

    return undefined;
  };

  return /*#__PURE__*/_react.default.createElement(_DataGrid.StyledTable, {
    withShadow: withShadow
  }, /*#__PURE__*/_react.default.createElement(_Header.default, {
    config: config,
    headerGroups: headerGroups
  }), getTableContent(), getFooterContent());
});

DataGrid.propTypes = {
  /** An array of column objects. This is one of the main ways to customize the grid */
  columns: _propTypes.default.arrayOf(_propTypes.default.shape({
    /** accessor or ID used -REQUIRED */
    accessor: _propTypes.default.string,

    /** The Label shown in the header */
    Header: _propTypes.default.string,

    /** Cell is a Component that will be used in a custom cell */
    Cell: _propTypes.default.func,

    /** The Filter is a Component that will be used in the filter dropdown */
    Filter: _propTypes.default.func,

    /** The filter(lowercase) is the function that will be used to filter */
    filter: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.func]),

    /** MUST BE SET TO FALSE TO USE FILTER */
    disableFilters: _propTypes.default.bool,

    /** an array used for select style filters */
    filterOptions: _propTypes.default.array
  })).isRequired,

  /** An Objects that determines which areas of the grid to show and some pagination variables */
  config: _propTypes.default.shape({
    /** option for providing a custom footer.
     *    If you provide this option, it will override the pagination footer.
     *    If we find that we need both the pagination footer and a custom one, we can revisit at a later date.
     */
    customFooter: _propTypes2.default.children,

    /** An Array of numbers to use in the items ber page select */
    pageSizes: _propTypes.default.array,

    /** default page size */
    pageSize: _propTypes.default.number,

    /** show headers. default is true */
    headers: _propTypes.default.bool,

    /** Text override for the message to display when no records are found */
    noRecordsText: _propTypes.default.string,

    /** show Search bar. default is false */
    search: _propTypes.default.bool,

    /** Function to utilize for search when enabled */
    searchFunction: _propTypes.default.func,

    /** An array of accessors to search through. Required for search, works best on columns with string values */
    searchColumns: _propTypes.default.arrayOf(_propTypes.default.string),

    /** column to sort by initially */
    sortBy: _propTypes.default.arrayOf(_propTypes.default.shape({})),

    /** show filters. default is false */
    filter: _propTypes.default.bool,

    /** show pagination. default is true */
    paginate: _propTypes.default.bool
  }),

  /** An Array of objects that each represent a row. The key must match a corresponding column.accessor in order to be shown */
  data: _propTypes.default.arrayOf(_propTypes.default.shape({})),

  /**
   * Trigger for displaying the loading spinner while data is loading.
   * Must be manually set/cleared.
   */
  isLoading: _propTypes.default.bool,

  /** Flag to enable a drop shadow */
  withShadow: _propTypes.default.bool
};
DataGrid.defaultProps = {
  config: {
    customFooter: undefined,
    headers: true,
    noRecordsText: undefined,
    pageSizes: [5, 10, 25],
    paginate: true,
    searchColumns: [],
    searchFunction: function searchFunction() {}
  },
  data: [],
  isLoading: false,
  withShadow: false
};
var _default = DataGrid;
exports.default = _default;